<template>
  <div class="vehicle-family-selector">
    <div class="selector-header">
      <h3>Add Vehicles by Family</h3>
      <p class="text-gray-600">
        Click on a family to preview and add all its vehicles at once
      </p>
    </div>

    <!-- Two Column Layout -->
    <div class="layout-two-column">
      <!-- Left Column: Families Selection -->
      <div class="column column-families">
        <!-- Family Search -->
        <div class="family-search">
          <input
            v-model="familySearchText"
            type="text"
            placeholder="Search families by name, code or series..."
            class="family-search-input"
            :disabled="loadingFamilies"
          />
          <span v-if="familySearchText" class="family-search-count">
            {{ filteredFamiliesForDropdown.length }} of
            {{ families.length }} families
          </span>
        </div>

        <!-- Loading Families -->
        <div v-if="loadingFamilies" class="families-loading">
          <div class="spinner"></div>
          <p>Loading families...</p>
        </div>

        <!-- All Families Grid -->
        <div v-else class="families-grid-container">
          <div
            v-for="(families, serie) in groupedFilteredFamilies"
            :key="serie"
            class="family-series-group"
          >
            <div class="series-label">{{ serie }}</div>
            <div class="families-grid">
              <div
                v-for="family in families"
                :key="family.catalogCode"
                @click="selectFamily(family)"
                class="family-card"
                :class="{
                  'family-card--selected': selectedCatalogCodes.includes(
                    family.catalogCode
                  )
                }"
              >
                <code class="family-card__code">{{ family.catalogCode }}</code>
                <span class="family-card__name">{{ family.family }}</span>
              </div>
            </div>
          </div>
          <div
            v-if="familySearchText && filteredFamiliesForDropdown.length === 0"
            class="families-no-results"
          >
            No families found matching "{{ familySearchText }}"
          </div>
        </div>
      </div>

      <!-- Right Column: Vehicle Preview -->
      <div class="column column-vehicles">
        <!-- Loading State -->
        <div v-if="loadingPreview" class="preview-loading">
          <div class="spinner"></div>
          <p>Loading family vehicles...</p>
        </div>

        <!-- Preview Section -->
        <div v-else-if="previewVehicles.length > 0" class="preview-section">
          <div class="preview-header">
            <div class="header-left">
              <h4 v-if="selectedCatalogCodes.length === 1">
                {{ selectedFamilies[0]?.family }}
              </h4>
              <h4 v-else>
                {{ selectedCatalogCodes.length }} Families Selected
              </h4>
              <span
                class="catalog-code-badge"
                v-if="selectedCatalogCodes.length > 1"
              >
                {{ selectedCatalogCodes.join(", ") }}
              </span>
              <span v-else class="catalog-code-badge"
                >Code: {{ selectedCatalogCodes[0] }}</span
              >
            </div>
            <div class="header-right">
              <span class="vehicle-count">
                {{ filteredPreviewVehicles.length }} vehicle{{
                  filteredPreviewVehicles.length !== 1 ? "s" : ""
                }}
              </span>
              <span v-if="alreadyAddedCount > 0" class="added-count">
                {{ alreadyAddedCount }} already added
              </span>
              <span v-if="newVehiclesCount > 0" class="new-count">
                {{ newVehiclesCount }} new
              </span>
            </div>
          </div>

          <!-- Filters -->
          <div class="preview-filters">
            <div class="filter-row">
              <label class="checkbox-label">
                <input type="checkbox" v-model="showOnlyNew" />
                Show only new vehicles (hide already added)
              </label>
            </div>
            <div class="filter-inputs">
              <input
                v-model="filters.year"
                type="text"
                placeholder="Filter by year..."
                class="filter-input"
              />
              <input
                v-model="filters.make"
                type="text"
                placeholder="Filter by make..."
                class="filter-input"
              />
              <input
                v-model="filters.model"
                type="text"
                placeholder="Filter by model..."
                class="filter-input"
              />
              <input
                v-model="filters.engine"
                type="text"
                placeholder="Filter by engine..."
                class="filter-input"
              />
            </div>
          </div>

          <!-- Preview Table -->
          <div class="preview-table-container">
            <table class="preview-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Year</th>
                  <th>Make</th>
                  <th>Model</th>
                  <th>Engine</th>
                  <th>Aspiration</th>
                  <th>Region</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="vehicle in filteredPreviewVehicles"
                  :key="vehicle.vehicleId"
                  :class="{ 'already-added': vehicle.alreadyAdded }"
                >
                  <td>
                    <code class="catalog-code">{{ vehicle.catalogCode }}</code>
                  </td>
                  <td>{{ vehicle.year }}</td>
                  <td>{{ vehicle.make }}</td>
                  <td>{{ vehicle.model }}</td>
                  <td>{{ vehicle.engineLiter }}L {{ vehicle.engineName }}</td>
                  <td>{{ vehicle.aspiration }}</td>
                  <td>{{ vehicle.region || "—" }}</td>
                  <td>
                    <span v-if="vehicle.alreadyAdded" class="badge badge-gray">
                      Already Added
                    </span>
                    <span v-else class="badge badge-green">New</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Actions -->
          <div class="preview-actions">
            <div class="action-info">
              <strong>Ready to add:</strong>
              {{ newVehiclesCount }} new vehicle{{
                newVehiclesCount !== 1 ? "s" : ""
              }}
              <span v-if="alreadyAddedCount > 0" class="skip-info">
                ({{ alreadyAddedCount }} will be skipped)
              </span>
            </div>
            <div class="action-buttons">
              <button @click="clearSelection" class="btn-secondary-button">
                Cancel
              </button>
              <button
                @click="addFamily"
                :disabled="adding || newVehiclesCount === 0"
                class="btn-add-family"
              >
                <span v-if="!adding">
                  ✓ Add {{ newVehiclesCount }} Vehicle{{
                    newVehiclesCount !== 1 ? "s" : ""
                  }}
                  to Product
                </span>
                <span v-else>⏳ Adding...</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div
          v-else-if="selectedCatalogCodes.length > 0 && !loadingPreview"
          class="empty-state"
        >
          <p>No vehicles found for selected families</p>
        </div>

        <!-- Initial State (no families selected) -->
        <div v-else class="initial-state">
          <p>
            Select one or more families from the left to preview their vehicles
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getVehicleFamilies,
  getVehiclesByFamily
} from "@/api/store/VehiclesService";
import { addFamilyApplications } from "@/api/store/ApplicationsService";

export default {
  name: "VehicleFamilySelector",
  props: {
    unified: {
      type: String,
      required: true
    },
    existingApplications: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      families: [],
      loadingFamilies: false,
      selectedCatalogCodes: [],
      familySearchText: "",
      previewVehicles: [],
      loadingPreview: false,
      adding: false,
      showOnlyNew: false,
      filters: {
        year: "",
        make: "",
        model: "",
        engine: ""
      }
    };
  },
  computed: {
    filteredFamiliesForDropdown() {
      if (!this.familySearchText) {
        return this.families;
      }

      const search = this.familySearchText.toLowerCase();
      return this.families.filter(
        f =>
          f.family.toLowerCase().includes(search) ||
          f.catalogCode.toLowerCase().includes(search) ||
          f.serie.toLowerCase().includes(search)
      );
    },
    groupedFilteredFamilies() {
      const grouped = {};
      this.filteredFamiliesForDropdown.forEach(family => {
        if (!grouped[family.serie]) {
          grouped[family.serie] = [];
        }
        grouped[family.serie].push(family);
      });
      return grouped;
    },
    groupedFamilies() {
      const grouped = {};
      this.families.forEach(family => {
        if (!grouped[family.serie]) {
          grouped[family.serie] = [];
        }
        grouped[family.serie].push(family);
      });
      return grouped;
    },
    selectedFamily() {
      return this.families.find(
        f => f.catalogCode === this.selectedCatalogCodes[0]
      );
    },
    selectedFamilies() {
      return this.families.filter(f =>
        this.selectedCatalogCodes.includes(f.catalogCode)
      );
    },
    existingVehicleKeys() {
      return new Set(
        this.existingApplications.map(
          app => `${app.make}-${app.model}-${app.year}-${app.engineName}`
        )
      );
    },
    filteredPreviewVehicles() {
      let filtered = this.previewVehicles;

      // Filter by "show only new"
      if (this.showOnlyNew) {
        filtered = filtered.filter(v => !v.alreadyAdded);
      }

      // Apply text filters
      if (this.filters.year) {
        filtered = filtered.filter(v =>
          String(v.year).includes(this.filters.year)
        );
      }
      if (this.filters.make) {
        filtered = filtered.filter(v =>
          v.make.toLowerCase().includes(this.filters.make.toLowerCase())
        );
      }
      if (this.filters.model) {
        filtered = filtered.filter(v =>
          v.model.toLowerCase().includes(this.filters.model.toLowerCase())
        );
      }
      if (this.filters.engine) {
        const engineSearch = this.filters.engine.toLowerCase();
        filtered = filtered.filter(v => {
          const engineStr = `${v.engineLiter}L ${v.engineName}`.toLowerCase();
          return engineStr.includes(engineSearch);
        });
      }

      return filtered;
    },
    alreadyAddedCount() {
      return this.previewVehicles.filter(v => v.alreadyAdded).length;
    },
    newVehiclesCount() {
      return this.previewVehicles.filter(v => !v.alreadyAdded).length;
    }
  },
  mounted() {
    this.loadFamilies();
  },

  methods: {
    async loadFamilies() {
      this.loadingFamilies = true;
      try {
        this.families = await getVehicleFamilies();
      } catch (error) {
        this.$toast.error("Error loading vehicle families");
        console.error(error);
      } finally {
        this.loadingFamilies = false;
      }
    },
    selectFamily(family) {
      const index = this.selectedCatalogCodes.indexOf(family.catalogCode);
      if (index > -1) {
        // Remove if already selected
        this.selectedCatalogCodes.splice(index, 1);
      } else {
        // Add if not selected
        this.selectedCatalogCodes.push(family.catalogCode);
      }
      this.loadFamilyPreview();
      // Scroll to preview if families selected
      if (this.selectedCatalogCodes.length > 0) {
        this.$nextTick(() => {
          const preview = this.$el.querySelector(
            ".preview-section, .preview-loading"
          );
          if (preview) {
            preview.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        });
      }
    },
    clearFamilySelection() {
      this.selectedCatalogCodes = [];
      this.previewVehicles = [];
    },
    async loadFamilyPreview() {
      if (this.selectedCatalogCodes.length === 0) {
        this.previewVehicles = [];
        return;
      }

      this.loadingPreview = true;
      try {
        // Load vehicles from all selected families
        const allVehicles = [];
        for (const catalogCode of this.selectedCatalogCodes) {
          const familyVehicles = await getVehiclesByFamily(catalogCode);
          allVehicles.push(...familyVehicles);
        }

        // Remove duplicates (same vehicle might be in multiple families)
        const uniqueVehicles = Array.from(
          new Map(
            allVehicles.map(v => [
              `${v.make}-${v.model}-${v.year}-${v.engineName}`,
              v
            ])
          ).values()
        );

        // Mark which ones are already added
        this.previewVehicles = uniqueVehicles.map(vehicle => ({
          ...vehicle,
          alreadyAdded: this.existingVehicleKeys.has(
            `${vehicle.make}-${vehicle.model}-${vehicle.year}-${vehicle.engineName}`
          )
        }));

        // Sort: new vehicles first, then by year desc
        this.previewVehicles.sort((a, b) => {
          if (a.alreadyAdded !== b.alreadyAdded) {
            return a.alreadyAdded ? 1 : -1;
          }
          return b.year - a.year;
        });
      } catch (error) {
        this.$toast.error("Error loading family vehicles");
        console.error(error);
      } finally {
        this.loadingPreview = false;
      }
    },
    async addFamily() {
      if (this.newVehiclesCount === 0) {
        this.$toast.warning(
          "All vehicles from selected families are already added"
        );
        return;
      }

      const familyNames = this.selectedFamilies.map(f => f.family).join(", ");
      if (
        !confirm(
          "Add " +
            this.newVehiclesCount +
            " new vehicle" +
            (this.newVehiclesCount !== 1 ? "s" : "") +
            ' from "' +
            familyNames +
            '"?'
        )
      ) {
        return;
      }

      this.adding = true;
      const totalFamilies = this.selectedCatalogCodes.length;
      let completed = 0;
      const failedFamilies = [];

      try {
        // Add vehicles from all selected families
        const results = [];
        for (const catalogCode of this.selectedCatalogCodes) {
          try {
            const familyObj = this.families.find(
              f => f.catalogCode === catalogCode
            );
            // Show progress toast
            this.$toast.info(
              `Adding family ${completed +
                1}/${totalFamilies}: ${familyObj?.family || catalogCode}...`,
              { timeout: 2000 }
            );

            const result = await addFamilyApplications(
              this.unified,
              catalogCode
            );
            results.push(result);
            completed++;
          } catch (error) {
            const familyObj = this.families.find(
              f => f.catalogCode === catalogCode
            );
            failedFamilies.push(familyObj?.family || catalogCode);
            console.error(`Error adding family ${catalogCode}:`, error);
          }
        }

        const totalAdded = results.reduce(
          (sum, r) => sum + (r.addedCount || 0),
          0
        );

        if (failedFamilies.length === 0) {
          this.$toast.success(
            `${totalAdded} vehicle${
              totalAdded !== 1 ? "s" : ""
            } from ${completed} ${
              completed !== 1 ? "families" : "family"
            } added successfully`
          );
        } else {
          this.$toast.warning(
            `Added ${totalAdded} vehicles from ${completed}/${totalFamilies} families. Failed: ${failedFamilies.join(
              ", "
            )}`
          );
        }

        this.$emit("family-added", results);
        this.clearSelection();
      } catch (error) {
        this.$toast.error(
          error.response?.data?.error || "Error adding families"
        );
        console.error(error);
      } finally {
        this.adding = false;
      }
    },
    clearSelection() {
      this.selectedCatalogCodes = [];
      this.previewVehicles = [];
      this.showOnlyNew = false;
      this.filters = {
        year: "",
        make: "",
        model: "",
        engine: ""
      };
    }
  }
};
</script>

<style lang="scss" scoped>
.vehicle-family-selector {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 1.5rem;
  margin-bottom: 1rem;
}

.selector-header {
  margin-bottom: 1.5rem;

  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 0.25rem;
  }

  p {
    font-size: 0.875rem;
  }
}

.layout-two-column {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  min-height: 600px;
}

.column {
  display: flex;
  flex-direction: column;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  overflow: hidden;
}

.column-families {
  background: #f9fafb;
  padding: 1rem;

  .family-search {
    margin-bottom: 1rem;
  }

  .families-grid-container {
    flex: 1;
    overflow-y: auto;
    border: none;
    padding: 0;
    margin-bottom: 0;
    max-height: unset;
  }
}

.column-vehicles {
  background: white;
  display: flex;
  flex-direction: column;

  .preview-section,
  .preview-loading,
  .empty-state,
  .initial-state {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .preview-section {
    padding: 1rem;
  }

  .preview-loading {
    justify-content: center;
    align-items: center;
    padding: 2rem;
  }

  .empty-state,
  .initial-state {
    justify-content: center;
    align-items: center;
    color: #9ca3af;
    font-size: 0.875rem;
  }
}

.initial-state {
  text-align: center;
  padding: 2rem;
}

.family-search {
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  .family-search-input {
    flex: 1;
    padding: 0.75rem 0.875rem;
    border: 2px solid #d1d5db;
    border-radius: 0.5rem;
    font-size: 1rem;
    transition: all 0.2s;

    &:focus {
      outline: none;
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }

    &:disabled {
      background-color: #f3f4f6;
      cursor: not-allowed;
    }
  }

  .family-search-count {
    font-size: 0.9375rem;
    color: #6b7280;
    white-space: nowrap;
    font-weight: 500;
  }
}

.families-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem;
  color: #6b7280;

  .spinner {
    width: 2rem;
    height: 2rem;
    border: 3px solid #e5e7eb;
    border-top-color: #3b82f6;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin-bottom: 0.5rem;
  }
}

.families-grid-container {
  max-height: 600px;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 0.75rem;
  margin-bottom: 1.5rem;
}

.family-series-group {
  margin-bottom: 1rem;

  &:last-child {
    margin-bottom: 0;
  }

  .series-label {
    padding: 0.5rem 0.75rem;
    background: #f3f4f6;
    font-size: 0.875rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #6b7280;
    border-radius: 0.25rem;
    margin-bottom: 0.5rem;
  }
}

.families-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 0.75rem;
}

.family-card {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  padding: 0.875rem 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.15s;
  background: white;
  min-height: 80px;
  justify-content: center;

  &:hover {
    border-color: #93c5fd;
    background: #eff6ff;
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(59, 130, 246, 0.12);
  }

  &--selected {
    border-color: #3b82f6;
    background: #dbeafe;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.25);

    &:hover {
      border-color: #2563eb;
      background: #dbeafe;
    }
  }

  &__code {
    font-family: monospace;
    font-size: 0.8125rem;
    font-weight: 700;
    color: #6b7280;
    background: #f3f4f6;
    padding: 0.25rem 0.5rem;
    border-radius: 0.25rem;
    width: fit-content;
  }

  &__name {
    font-size: 0.9375rem;
    color: #374151;
    font-weight: 600;
    line-height: 1.35;
  }
}

.families-no-results {
  text-align: center;
  padding: 2rem;
  color: #6b7280;
  font-size: 0.875rem;
}

.preview-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem;
  color: #6b7280;

  .spinner {
    width: 2rem;
    height: 2rem;
    border: 3px solid #e5e7eb;
    border-top-color: #3b82f6;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin-bottom: 0.5rem;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.preview-section {
  .preview-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 0.75rem;
    padding: 0.75rem;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 0.375rem;
    color: white;

    .header-left {
      display: flex;
      flex-direction: column;
      gap: 0.375rem;

      h4 {
        font-size: 1rem;
        font-weight: 600;
        margin: 0;
      }

      .catalog-code-badge {
        display: inline-block;
        background: rgba(255, 255, 255, 0.2);
        padding: 0.2rem 0.5rem;
        border-radius: 1rem;
        font-size: 0.75rem;
        font-weight: 600;
        backdrop-filter: blur(10px);
        width: fit-content;
      }
    }

    .header-right {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 0.2rem;
      text-align: right;

      .vehicle-count {
        font-size: 0.875rem;
        font-weight: 600;
      }

      .added-count {
        font-size: 0.75rem;
        opacity: 0.9;
      }

      .new-count {
        font-size: 0.75rem;
        background: rgba(16, 185, 129, 0.9);
        padding: 0.15rem 0.5rem;
        border-radius: 1rem;
        font-weight: 600;
      }
    }
  }

  .preview-filters {
    margin-bottom: 0.75rem;
    padding: 0.5rem;
    background: #f9fafb;
    border-radius: 0.375rem;

    .filter-row {
      margin-bottom: 0.5rem;
    }

    .checkbox-label {
      display: flex;
      align-items: center;
      font-size: 0.8rem;
      cursor: pointer;

      input[type="checkbox"] {
        margin-right: 0.5rem;
      }
    }

    .filter-inputs {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 0.375rem;
    }

    .filter-input {
      padding: 0.375rem;
      border: 1px solid #d1d5db;
      border-radius: 0.375rem;
      font-size: 0.75rem;

      &:focus {
        outline: none;
        border-color: #3b82f6;
        box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
      }

      &::placeholder {
        color: #9ca3af;
      }
    }
  }

  .preview-table-container {
    flex: 1;
    overflow-y: auto;
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
    margin-bottom: 0.75rem;
  }

  .preview-table {
    width: 100%;
    font-size: 1.1rem;

    thead {
      background: #f9fafb;
      position: sticky;
      top: 0;
      z-index: 1;

      th {
        padding: 0.5rem;
        text-align: left;
        font-weight: 600;
        border-bottom: 1px solid #e5e7eb;
        white-space: nowrap;
      }
    }

    tbody {
      tr {
        &:hover {
          background: #f9fafb;
        }

        &.already-added {
          opacity: 0.6;
          background: #f3f4f6;
        }

        td {
          padding: 0.5rem;
          border-bottom: 1px solid #f3f4f6;
        }
      }
    }

    .catalog-code {
      background: #f3f4f6;
      padding: 0.2rem 0.375rem;
      border-radius: 0.25rem;
      font-family: monospace;
      font-size: 0.7rem;
      color: #374151;
      font-weight: 600;
    }
  }

  .badge {
    display: inline-block;
    padding: 0.15rem 0.4rem;
    border-radius: 9999px;
    font-size: 0.7rem;
    font-weight: 500;

    &.badge-green {
      background: #d1fae5;
      color: #065f46;
    }

    &.badge-gray {
      background: #e5e7eb;
      color: #4b5563;
    }
  }

  .preview-actions {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding: 0.75rem;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;

    .action-info {
      flex: 1;
      font-size: 0.8rem;

      strong {
        color: #374151;
        font-weight: 600;
      }

      .skip-info {
        color: #6b7280;
        font-size: 0.75rem;
        margin-left: 0.375rem;
      }
    }

    .action-buttons {
      display: flex;
      gap: 0.5rem;
    }
  }
}

.btn-add-family {
  padding: 0.625rem 1.25rem;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  border-radius: 0.375rem;
  font-weight: 600;
  font-size: 0.8125rem;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(16, 185, 129, 0.3);
  white-space: nowrap;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(16, 185, 129, 0.4);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
}

.empty-state {
  text-align: center;
  padding: 2rem;
  color: #6b7280;
}

.btn-success-button,
.btn-secondary-button {
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-weight: 500;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.btn-success-button {
  background: #10b981;
  color: white;
  border: none;

  &:hover:not(:disabled) {
    background: #059669;
  }
}

.btn-secondary-button {
  background: white;
  color: #374151;
  border: 1px solid #d1d5db;

  &:hover {
    background: #f9fafb;
  }
}
</style>
